import { firstOrDefault, getIdOrNull, requestWithPromise } from '..'
import { getDataOrDefault, HttpMethods, makeQueryString } from '../apiHelper'
import { isEncoder, isHospitalAdmin } from '../roles'
import apiClient from './apiClient'

export const RestoreUserMessages = Object.freeze({
  RESTORE_EMAIL_SENT: 'restore_email_sent',
  DEFAULT: 'on_success'
})

export const RestoreUserWithTokenErrors = Object.freeze({
  OTHER: 'other',
  NO_TOKEN_PROVIDED: 'no_token_provided',
  RESTORE_USER_PERMISSION_DENIED: 'restore_user_permission_denied',
  TOKEN_ALREADY_CONSUMED: 'token_already_consumed',
  TOKEN_EXPIRED: 'token_expired'
})

export const createUser = (user, newUser, disabledErrorHandler = true, followError = true) => {
  return requestWithPromise(
    '/user/create',
    HttpMethods.POST,
    newUser,
    user,
    disabledErrorHandler,
    followError
  )
}

export const deleteUser = (user, userToDelete) => apiClient.delete(user, `api/User/${getIdOrNull(userToDelete)}`)

export const getRoles = user => {
  return requestWithPromise(
    '/api/roles',
    HttpMethods.GET,
    null,
    user
  )
}

export const getUsers = (filter, user, excludeAdmins = 0, role = null) => {
  let url = `/users?excludeAdminUsers=${excludeAdmins}&newFilter=${filter}`

  if (typeof role === 'string') {
    url += '&role=' + role
  }

  return requestWithPromise(
    url,
    HttpMethods.GET,
    null,
    user
  )
}

export const getMe = token => {
  return apiClient.get({ token, switchUser: null }, 'user/me')
}

export const getPaginateUsers = (user, parameters) => {
  return requestWithPromise(
    `/api/users${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const addSupervisorStudents = (user, body, currentUser) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/supervised-students`,
    HttpMethods.POST,
    body,
    currentUser
  )
}

export const deleteSupervisorStudents = (user, supervisor, student) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(supervisor)}/supervised-students/${getIdOrNull(student)}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const getSupervisorStudents = (user, currentUser, valueSearch = '') => {
  const search = typeof valueSearch !== 'undefined' ? `?search=${valueSearch}` : ''

  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/supervised-students${search}`,
    HttpMethods.GET,
    null,
    currentUser
  ).then(getDataOrDefault({}))
}

export const getCountSupervisorStudents = user => {
  return requestWithPromise(
    '/user/supervised-students-count',
    HttpMethods.GET,
    null,
    user
  ).then(getDataOrDefault({}))
}

export const getUserOptins = user => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/optins`,
    HttpMethods.GET,
    null,
    user
  )
}

export const createUserOptin = (optin, user) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/optins`,
    HttpMethods.POST,
    optin.toRequestBody(),
    user
  )
}

export const updateUser = (user, body, disabledErrorHandler = false, followError = false) => {
  return requestWithPromise(
    `/api/User/${getIdOrNull(body)}`,
    HttpMethods.PATCH,
    body,
    user,
    disabledErrorHandler,
    followError
  )
}

export const updateUserOptin = (optin, user) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/optins/${getIdOrNull(optin)}`,
    HttpMethods.PATCH,
    optin.toRequestBody(),
    user
  )
}

export const getUserSectors = (user, targetUser) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/sectors`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getUserSupervisedSectors = (selectedUser, user, parameters = {}) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(selectedUser)}/supervised-sectors${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const setUserSupervisedSectors = (sectors, supervisorUser, user) => {
  return requestWithPromise(
    `/user/set-supervisor-sectors/${getIdOrNull(supervisorUser)}`,
    HttpMethods.POST,
    { sectors: sectors.map(s => s.id) },
    user
  )
}

export const restoreUserByEmail = (user, userToRestore) => {
  let destination = null

  if (isHospitalAdmin(user)) {
    destination = getIdOrNull(firstOrDefault(user.institutions))
  } else if (isEncoder(user)) {
    destination = getIdOrNull(user.school)
  }

  return apiClient.patch(user, 'user/restore', { email: userToRestore.email, destination })
}

export const getTutoredSectors = (user, targetUser) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/tutored-sectors`,
    HttpMethods.GET,
    null,
    user
  )
}

export const linkTutoredSectors = (user, targetUser, sectors) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/tutored-sectors`,
    HttpMethods.POST,
    { ids: sectors },
    user
  )
}

export const unLinkTutoredSectors = (user, targetUser, sectors) => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(targetUser)}/tutored-sectors${makeQueryString({ ids: sectors })}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const getUserUnreadMessages = user => {
  return requestWithPromise(
    `/api/users/${getIdOrNull(user)}/unread-messages`,
    HttpMethods.GET,
    null,
    user
  )
}

export const readAllUserMessages = user => {
  return requestWithPromise(
    '/internship-message/read-all',
    HttpMethods.POST,
    {},
    user
  )
}

const USER_COORDINATED_SECTIONS_PARAMS = { search: '' }
export const getUserCoordinatedSections = (user, targetUser, parameters = USER_COORDINATED_SECTIONS_PARAMS) => {
  return apiClient.get(user, `api/users/${getIdOrNull(targetUser)}/coordinated-sections`, parameters)
}

export const linkUserToSections = (user, targetUser, sectionIds) => {
  return apiClient.post(user, `api/users/${getIdOrNull(targetUser)}/coordinated-sections`, { ids: sectionIds })
}

export const unlinkSectionFromUser = (user, targetUser, section) => {
  return apiClient.delete(user, `api/users/${getIdOrNull(targetUser)}/coordinated-sections/${getIdOrNull(section)}`)
}

export const restoreUserWithToken = (token) => apiClient.patch(null, `api/internal-tokens/${token}/consume`)
export const getInternshipRequestByToken = (token) => apiClient.get(null, `api/internal-tokens/${token}/individual-internship-request`)
